import { useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { App, Dropdown, Modal } from 'antd';
import { Form, Formik, FormikValues } from 'formik';
import { useAtomValue } from 'jotai';
import * as Yup from 'yup';

import { ReactComponent as Menu } from '../../assets/menu.svg';
import Button from '../../custom/button/button';
import Input from '../../custom/input/input';
import { adminChangePassword } from '../../requests';
import { userAtom } from '../../utils/store';
import { validator } from '../../utils/validator';
import styles from './dashboardLayout.module.scss';

const formatDate = () =>
  new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

const getInitials = (display: string) => {
  const parts = display.trim().split(/\s+/);
  return parts.length >= 2
    ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    : display.slice(0, 2).toUpperCase();
};

const getDisplayName = (email: string) => {
  const local = email?.split('@')[0] ?? '';
  return local
    .replace(/[._-]/g, ' ')
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const validationSchema = Yup.object().shape({
  currentPassword: validator.password,
  newPassword: validator.newPassword,
  confirmNewPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Passwords must match')
    .required('Please confirm your new password'),
});

const Header = ({ handleOpenSidebar }: { handleOpenSidebar: () => void }) => {
  const user = useAtomValue(userAtom);
  const { notification } = App.useApp();
  const [modalOpen, setModalOpen] = useState(false);

  const displayName = getDisplayName(user?.email ?? '');
  const initials = getInitials(displayName || user?.email?.slice(0, 2) || 'AD');
  const role = user?.role?.[0] ?? '';

  const changePasswordMutation = useMutation({
    mutationKey: ['admin-change-password'],
    mutationFn: adminChangePassword,
  });

  const handleChangePassword = async (values: FormikValues, resetForm: () => void) => {
    const payload: AdminChangePasswordPayload = {
      email: user?.email ?? '',
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
      confirmNewPassword: values.confirmNewPassword,
    };

    try {
      await changePasswordMutation.mutateAsync(payload, {
        onSuccess: (data) => {
          notification.success({ message: 'Success', description: data?.message });
          setModalOpen(false);
          resetForm();
        },
      });
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error?.response?.data?.message,
      });
    }
  };

  const dropdownItems = [
    {
      key: 'change-password',
      label: 'Change Password',
      onClick: () => setModalOpen(true),
    },
  ];

  return (
    <>
      <header className={styles.header}>
        <section className={styles.headerMenuGroup}>
          <button onClick={handleOpenSidebar} className={styles.menuBtn}>
            <Menu />
          </button>
        </section>

        <section className={styles.headerGroup}>
          {/* Current date */}
          <span style={{ color: '#ae252e', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
            {formatDate()}
          </span>

          {/* Divider */}
          <span style={{ width: 1, height: '1.5rem', background: '#e4e7ec', display: 'inline-block' }} />

          {/* Role badge */}
          {role && (
            <span
              style={{
                background: '#f2f4f7',
                border: '1px solid #e4e7ec',
                borderRadius: '1rem',
                color: '#344054',
                fontSize: '0.75rem',
                fontWeight: 500,
                padding: '0.2rem 0.75rem',
                whiteSpace: 'nowrap',
              }}
            >
              {role}
            </span>
          )}

          {/* Avatar + name — click to open dropdown */}
          <Dropdown menu={{ items: dropdownItems }} trigger={['click']} placement="bottomRight">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <div
                style={{
                  width: '2.25rem',
                  height: '2.25rem',
                  borderRadius: '50%',
                  background: '#ce9938',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.8125rem',
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {initials}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                <span style={{ fontWeight: 600, fontSize: '0.875rem', color: '#101828', whiteSpace: 'nowrap' }}>
                  {displayName || 'Admin'}
                </span>
                {user?.email && (
                  <span style={{ fontSize: '0.75rem', color: '#667085', whiteSpace: 'nowrap' }}>
                    {user.email}
                  </span>
                )}
              </div>
            </div>
          </Dropdown>
        </section>
      </header>

      <Modal
        title="Change Password"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Formik
          initialValues={{ currentPassword: '', newPassword: '', confirmNewPassword: '' }}
          validationSchema={validationSchema}
          onSubmit={(values, { resetForm }) => handleChangePassword(values, resetForm)}
        >
          {() => (
            <Form className="fields" style={{ padding: '1rem 0' }}>
              <Input
                name="currentPassword"
                type="password"
                label="Current Password"
                placeholder="Enter current password"
              />
              <Input
                name="newPassword"
                type="password"
                label="New Password"
                placeholder="Enter new password"
              />
              <Input
                name="confirmNewPassword"
                type="password"
                label="Confirm New Password"
                placeholder="Confirm new password"
              />
              <Button
                type="submit"
                text="Change Password"
                disabled={changePasswordMutation.isPending}
                isLoading={changePasswordMutation.isPending}
              />
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  );
};

export default Header;
